/**
 * Product Description Read More Toggle
 * Handles expand/collapse functionality and auto-collapse on scroll
 */

class ProductDescriptionReadMore {
  constructor() {
    this.containers = document.querySelectorAll('[data-read-more-container]');
    this.observers = new Map();
    this.toggleHandlers = new Map();
    this.init();
  }

  init() {
    if (this.containers.length === 0) {
      console.log('ProductDescriptionReadMore: No containers found');
      return;
    }

    console.log(`ProductDescriptionReadMore: Found ${this.containers.length} container(s)`);

    this.containers.forEach((container, index) => {
      const wrapper = container.closest('.product-description-wrapper');
      if (!wrapper) {
        console.log(`ProductDescriptionReadMore: No wrapper found for container ${index}`);
        return;
      }

      const toggle = wrapper.querySelector('[data-read-more-toggle]');
      if (!toggle) {
        console.log(`ProductDescriptionReadMore: No toggle found for container ${index}`);
        return;
      }

      console.log(`ProductDescriptionReadMore: Initializing container ${index}`);

      // Wait for content to be fully rendered
      const checkContent = () => {
        // Temporarily remove collapsed state to measure full height
        container.removeAttribute('data-collapsed');
        const fullHeight = container.scrollHeight;
        
        // Apply collapsed state to measure collapsed height
        container.setAttribute('data-collapsed', 'true');
        // Force reflow
        void container.offsetHeight;
        const collapsedHeight = container.scrollHeight;
        
        // Check if content needs truncation (if full height is greater than collapsed height)
        if (fullHeight <= collapsedHeight || Math.abs(fullHeight - collapsedHeight) < 10) {
          // Content is short enough, hide the toggle
          toggle.style.display = 'none';
          container.removeAttribute('data-collapsed');
          return;
        }

        // Show toggle and initialize as collapsed
        toggle.style.display = 'inline-block';
        container.setAttribute('data-collapsed', 'true');
        toggle.setAttribute('aria-expanded', 'false');

        // Remove any existing click handler for this toggle
        if (this.toggleHandlers.has(index)) {
          toggle.removeEventListener('click', this.toggleHandlers.get(index));
        }

        // Create click handler
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('ProductDescriptionReadMore: Toggle clicked');
          this.handleToggle(container, toggle);
        };

        // Store handler and add event listener
        this.toggleHandlers.set(index, clickHandler);
        toggle.addEventListener('click', clickHandler);
        console.log(`ProductDescriptionReadMore: Click handler attached to toggle ${index}`);

        // Set up Intersection Observer for auto-collapse
        this.setupObserver(container, toggle, index);
      };

      // Check immediately and also after delays to handle dynamic content
      checkContent();
      setTimeout(checkContent, 100);
      setTimeout(checkContent, 500);
    });
  }

  handleToggle(container, toggle) {
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    const textElement = toggle.querySelector('.read-more-text');
    
    if (isExpanded) {
      // Collapse
      container.setAttribute('data-collapsed', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      if (textElement) {
        textElement.textContent = 'Read more';
      }
    } else {
      // Expand
      container.setAttribute('data-collapsed', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      if (textElement) {
        textElement.textContent = 'Read less';
      }
    }
  }

  setupObserver(container, toggle, index) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // If element is out of viewport and is expanded, collapse it
          if (!entry.isIntersecting && toggle.getAttribute('aria-expanded') === 'true') {
            container.setAttribute('data-collapsed', 'true');
            toggle.setAttribute('aria-expanded', 'false');
            const textElement = toggle.querySelector('.read-more-text');
            if (textElement) {
              textElement.textContent = 'Read more';
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0
      }
    );

    observer.observe(container);
    this.observers.set(index, observer);
  }

  destroy() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
  }
}

// Initialize when DOM is ready
function initReadMore() {
  // Wait a bit for any dynamic content to load
  setTimeout(() => {
    try {
      new ProductDescriptionReadMore();
    } catch (error) {
      console.error('Error initializing ProductDescriptionReadMore:', error);
    }
  }, 100);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReadMore);
} else {
  // DOM already loaded
  if (window.requestIdleCallback) {
    requestIdleCallback(initReadMore);
  } else {
    initReadMore();
  }
}
